<script setup lang="ts">
import { Form } from '@primevue/forms';
import { type FormSubmitEvent } from '@primevue/forms/form';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { Checkbox, FloatLabel, InputText } from 'primevue';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

const { t } = useI18n();

const model = defineModel<string[]>({ default: [] });

type Feature = Readonly<{ id: string; labelKey: string }>;
const features: ReadonlyArray<Feature> = [
    { id: 'calendar_viewer', labelKey: 'questionnaire.features.calendarViewer' },
    { id: 'dashboard_creator', labelKey: 'questionnaire.features.dashboardCreator' },
    { id: 'eva_report', labelKey: 'questionnaire.features.evaReport' },
    { id: 'progress_update_tool', labelKey: 'questionnaire.features.progressUpdateTool' },
    { id: 'project_comparison', labelKey: 'questionnaire.features.projectComparison' },
    { id: 'schedule_integrity_checks', labelKey: 'questionnaire.features.scheduleIntegrityChecks' },
    { id: 'schedule_sharing', labelKey: 'questionnaire.features.scheduleSharing' },
    { id: 'schedule_viewer', labelKey: 'questionnaire.features.scheduleViewer' }
] as const;

const checked = ref<string[]>([]);
const otherText = ref('');

const resolver = zodResolver(
    z.object({
        other: z
            .string()
            .nullable()
            .transform((value) => value ?? '')
            .refine((value) => value === '' || value.length >= 3, t('error.minimumThreeCharacters'))
    })
);

defineProps<{ onFormSubmit: (event: FormSubmitEvent) => void }>();

watch([checked, otherText], () => {
    const other = (otherText.value ?? '').trim();
    model.value = [...(checked.value ?? []), ...(other ? [other] : [])];
});
</script>

<template>
    <Form
        v-slot="$form"
        :resolver="resolver"
        :validateOnValueUpdate="false"
        :validateOnBlur="true"
        @submit="onFormSubmit">
        <div class="flex flex-col gap-3">
            <p class="text-surface-500 mt-1">
                {{ t('checkAllThatApply') }}
            </p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div v-for="feature in features" :key="feature.id" class="flex items-center gap-2">
                    <Checkbox v-model="checked" :inputId="feature.id" :name="feature.id" :value="feature.id" />
                    <label :for="feature.id">{{ t(feature.labelKey) }}</label>
                </div>
            </div>
            <div class="flex flex-col gap-1">
                <FloatLabel variant="on">
                    <InputText v-model="otherText" inputId="other" name="other" type="text" fluid />
                    <label for="other">{{ t('questionnaire.features.other') }}</label>
                </FloatLabel>
                <Message v-if="$form.other?.invalid" severity="error" size="small" variant="simple">{{
                    $form.other.error.message
                }}</Message>
            </div>
        </div>
    </Form>
</template>
